import { FC } from 'react'
import { GRAY_400, GRAY_600, GRAY_900 } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { WorkspaceSeatType } from 'models/user'
import { useTranslation } from 'react-i18next'

/** Copy (workspace_profile namespace) shown for each selectable seat type in the invite form. */
const SEAT_TYPE_COPY: Record<WorkspaceSeatType, { title: string; subTitle: string }> = {
  [WorkspaceSeatType.FULL]: { title: 'full_access', subTitle: 'add_members_full_access_description' },
  [WorkspaceSeatType.SPONSORED]: { title: 'sponsored_access', subTitle: 'sponsored_access_description' },
  [WorkspaceSeatType.RESTRICTED]: { title: 'restricted_access', subTitle: 'restricted_access_description' },
}

/**
 * @interface Props Input properties
 */
interface Props {
  /** Seat types the current plan allows selecting (ISSUE-2078) */
  allowedSeatTypes: WorkspaceSeatType[]
  /** Currently selected seat type */
  selectedSeatType: WorkspaceSeatType
  /** onChange action triggered when the user picks a seat type */
  onChange?: (seatType: WorkspaceSeatType) => void
  /** Whether the dropdown should be non-interactive */
  disabled: boolean
}

/**
 * @component Seat-type selector for the invite-members form. Shows only the seat types
 * permitted by the workspace plan and never upsells — it is purely for selection (ISSUE-2078).
 * @example
 *  <AddMembersRole allowedSeatTypes={allowed} selectedSeatType={seatType} onChange={setSeatType} disabled={false} />
 */
export const AddMembersRole: FC<Props> = ({ allowedSeatTypes, selectedSeatType, onChange, disabled = false }) => {
  const { t } = useTranslation(['workspace_profile', 'profile'])

  // With a single permitted seat type there is nothing to choose, so the control is inert.
  const isSelectable = !disabled && allowedSeatTypes.length > 1

  // Restricted is the only free seat type; Full and Sponsored are paid.
  const renderPaidFreeBadge = (seatType: WorkspaceSeatType) => {
    const isPaid = seatType !== WorkspaceSeatType.RESTRICTED
    return <MUIBadge label={t(isPaid ? 'profile:members_content.badge_paid' : 'profile:members_content.badge_free')} color={isPaid ? 'blue' : 'gray'} size="sm" sx={{ flexShrink: 0 }} />
  }

  return (
    // The field sizes to its content (bounded by the modal) so longer DE/FR label+badge combinations never wrap or collide with the chevron.
    // The 220px floor must itself be viewport-bounded: min-width beats max-width in CSS, so a bare 220 would overflow modals narrower than that.
    <Stack sx={{ width: 'fit-content', minWidth: 'min(220px, 100%)', maxWidth: '100%' }}>

      <Typography sx={{ marginBottom: 0.4 }} variant="text-md" fontWeight={'medium'}>
        {t('access_setting')}
      </Typography>

      <MUIDropdown
        disablePortal={false}
        placement="top-start"
        sx={{ zIndex: 1000 }}
        button={(isOpen, action) => (
          <BorderBoxWrapper
            borderColor={GRAY_400}
            component="div"
            padding="0.6rem 1.2rem"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.8rem',
              transition: '.3s ease',
              cursor: isSelectable ? 'pointer' : 'default',
            }}
            onClick={(e) => {
              if (isSelectable) return action(e)
            }}
          >
            {/* flexWrap lets the badge drop below the label on viewports too narrow for both, instead of overflowing into the chevron */}
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0, flexWrap: 'wrap' }}>

              <Typography variant="text-md" color={isSelectable ? GRAY_900 : GRAY_600} fontWeight="medium">
                {t(SEAT_TYPE_COPY[selectedSeatType].title)}
              </Typography>

              {renderPaidFreeBadge(selectedSeatType)}

            </Stack>

            {isSelectable && (isOpen ? <KeyboardArrowUpRoundedIcon fontSize="large" sx={{ flexShrink: 0 }} /> : <KeyboardArrowDownRoundedIcon fontSize="large" sx={{ flexShrink: 0 }} />)}
          </BorderBoxWrapper>
        )}
      >
        {allowedSeatTypes.map((seatType) => (
          <MUIDropdownItem key={seatType} isSelected={seatType === selectedSeatType} onClick={() => onChange?.(seatType)}>
            <Stack gap=".2rem">

              <Stack direction="row" alignItems="center" gap={0.5}>

                <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                  {t(SEAT_TYPE_COPY[seatType].title)}
                </Typography>

                {renderPaidFreeBadge(seatType)}

              </Stack>

              <Typography variant="text-sm" color={GRAY_900}>
                {t(SEAT_TYPE_COPY[seatType].subTitle)}
              </Typography>

            </Stack>
          </MUIDropdownItem>
        ))}
      </MUIDropdown>
    </Stack>
  )
}
